/**
 * YourProgressBlock — at-a-glance summary of the member's Community Goal journey:
 * journeys joined, stops stamped and rewards unlocked. Purely a read-only
 * presentation of existing data (no new business logic).
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { BookMarked, Stamp, Gift } from "lucide-react";

interface ProgressSummary {
  journeys: number;
  stamps: number;
  rewards: number;
}

export function YourProgressBlock() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["your-progress-summary", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<ProgressSummary> => {
      const [parts, visits, redemptions] = await Promise.all([
        supabase
          .from("common_goal_participants")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user!.id),
        supabase
          .from("discovery_visits" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user!.id),
        supabase
          .from("offer_redemptions" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user!.id),
      ]);
      return {
        journeys: parts.count ?? 0,
        stamps: (visits as any).count ?? 0,
        rewards: (redemptions as any).count ?? 0,
      };
    },
  });

  if (!user) return null;
  if (isLoading) return <Skeleton className="h-24 w-full rounded-2xl" />;
  if (!data) return null;

  const items = [
    { icon: BookMarked, label: "Journeys joined", value: data.journeys },
    { icon: Stamp, label: "Stops stamped", value: data.stamps },
    { icon: Gift, label: "Rewards unlocked", value: data.rewards },
  ];

  return (
    <Card className="rounded-2xl">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="t-eyebrow">
            Your progress
          </p>
          <button
            type="button"
            onClick={() => navigate("/common-goals")}
            className="text-xs font-semibold text-primary hover:underline"
          >
            View journeys
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {items.map(({ icon: Icon, label, value }) => (
            <div key={label} className="rounded-xl bg-muted/60 p-2.5 text-center">
              <Icon className="h-4 w-4 mx-auto text-primary" />
              <p className="text-lg font-extrabold leading-tight mt-1">{value}</p>
              <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default YourProgressBlock;
